<template>
  <div class="content-wrapper traffic-sources-container">

    <!-- Section Header -->
    <div class="section-header d-flex justify-content-between align-items-center">
      <div class="section-header__content d-flex">
        <h1 class="section-header__content__title">Traffic Sources</h1>
      </div>
    </div>

    <loading-skeleton
      v-if="preloader"
      type="table"
      :table="{ headItems: 6, bodyItems: 20, filters: false, search: true }"
    />
    <div v-else class="optimizer-table traffic-sources-table">
      <vue-opti-table-light
        name="trafficSourceAccountsTable"
        class="table-wrapper"
        :hover="true"
        :header-fields="table.fields"
        :resized-columns="resizedColumns"
        @resize="$_handleColumnsResize($event)"
        :items="table.items" v-model="tableModel"
        :default-rows="100"
        sticky
      >
        <template #name="{ item }">
          <div class="d-flex align-items-center">
            <LiveEditText
              class="w-100 mr-3"
              v-model="item.name"
              :key="item.name"
              :beforeBlur="(value) => onAccountNameChange(value, item)"
            />
            <span class="accountId-text" v-if="item.updated" v-b-tooltip.hover.top
                  title="(Updated)"
            > (Updated) </span>
            <span class="accountId-text" v-if="showAccountId(item)" v-b-tooltip.hover.top
                  :title="`${item.trafficSourceType.uniqueName} Ad Account Id`"
            > - {{ item.trafficSourceType.uniqueName === 'GoogleAds' ? item.credentials.googleCustomerId : item.settings.adAccountId }}</span>
          </div>
        </template>
        <template #status="{ item }">
          <span v-if="item.status === 1" class="status-item status-item-enabled"><span class="circle" /> Enabled</span>
          <span v-else-if="item.status === 0" class="status-item status-item-disabled"><span class="circle" /> Disabled</span>
          <span v-else-if="item.status === -1" class="status-item status-item-deleted"><span class="circle" /> Deleted</span>
        </template>
        <template #type="{ item }">
          <div class="acc-item">
            <img class="acc-item-icon new-icon" :src="getTrafficSourceLogo(item.type.uniqueName)" alt="traffic-source-logo" />
            <div class="acc-item-name">{{ item.type.name }}</div>
          </div>
        </template>
        <template #linkedTrackers="{ item }">
          <template v-if="displayedLinkedTrackers[item.id].length > 0">
            <b-btn v-for="tracker in displayedLinkedTrackers[item.id].slice(0, 2)" size="sm" variant="primary"
                   class="linked-tracker-btn position-relative"
                   :key="item.id + '-' + tracker.id + '-' + tracker.sourceIdOnTracker"
            >
              <img :src="tracker.uniqueName === 'ClickFlare' ? `/sh_static/accounts/ClickFlare.svg` : `/sh_static/accounts/${tracker.uniqueName}.png`" />{{ tracker.name }}
              <div v-if="getTotalLinkedNumber(tracker, item) > 1" class="position-absolute badge badge-info badge-pill">
                {{ getTotalLinkedNumber(tracker, item) }}
              </div>
            </b-btn>
            <MoreItems v-if="displayedLinkedTrackers[item.id].length > 2">
              <b-btn v-for="tracker in displayedLinkedTrackers[item.id].slice(2)" size="sm" variant="primary"
                     class="linked-tracker-btn w-100 position-relative m-0 text-truncate"
                     :key="item.id + '-' + tracker.id + '-' + tracker.sourceIdOnTracker"
              >
                <img :src="`/sh_static/accounts/${tracker.uniqueName}.png`" />{{ tracker.name }}
                <div v-if="getTotalLinkedNumber(tracker, item) > 1" class="position-absolute badge badge-info badge-pill">
                  {{ getTotalLinkedNumber(tracker, item) }}
                </div>
              </b-btn>
            </MoreItems>
          </template>
          <template v-else>
            <i class="text-muted">( No linked trackers )</i>
          </template>
        </template>
        <template #actions="{ item, i }">
          <div class="d-flex justify-content-center">
            <switch-button
              :checked="item.status"
              :enabled-value="1"
              :disabled-value="0"
              :hover-title="{ enabled: 'Stop', disabled: 'Start' }"
              :update="(value) => $_changeStatus(item, value)"
            />
            <action-btn type="edit" :click="() => $_editAccount(item, i)" />
          </div>
        </template>
      </vue-opti-table-light>
      <br>
      <h4 v-if="!table || !table.items || !table.items.length" class="lead">
        <a style="color: #252D41">You do not have any traffic sources integrated yet. To start adding your integrations go to <a v-if="docUrl" @click="docUrl" class="accountWizardUrl" style="color: #4158D0"><b>Account Wizard.</b></a></a>
      </h4>
    </div>
    <TrafficSourceModal ref="refTrafficSourceModal" @submit="$_updateAccount" @onTableUpdate="$_load" />
  </div>
</template>

<script>
import notifications from '@sh/mixins/notifications';
import configFile from '@sh/components/TrafficSources/config';
import TrafficSourceModal from '@sh/components/TrafficSources/TrafficSourceModal';
import moment from 'moment';
import { uniqBy } from 'lodash';
import LiveEditText from '@sh/components/Utils/LiveEditText.ts.vue';
import { getTrafficSourceLogo } from '@sh/helpers';
import MoreItems from '@sh/components/Utils/MoreItems.ts.vue';

export default {
  name: 'Trafficsources',
  components: {
    TrafficSourceModal,
    LiveEditText,
    MoreItems,
  },
  mixins: [notifications],
  data() {
    return {
      preloader: true,
      config: null,
      tableModel: {
        selectedRows: [],
        displayColumns: [],
        columnsOrder: [],
      },
      resizedColumns: {},
      table: {
        fields: [
          {
            header: { content: 'Id', style: '' },
            display: true,
            item: {
              key: 'id',
              sortable: true,
              content: (item) => item.id,
              style: { textAlign: 'center' },
            },
          },
          {
            header: { content: 'Actions', style: 'width: 1%' },
            display: true,
            item: {
              key: 'actions',
              slot: 'actions',
              style: { textAlign: 'center' },
            },
          },
          {
            header: { content: 'Account Name', style: '' },
            display: true,
            item: {
              key: 'name',
              slot: 'name',
              cellClass: 'cell-name',
              sortable: true,
              searchable: true,
              content: (item) => item.name,
            },
          },
          {
            header: { content: 'Status', style: '' },
            display: true,
            item: {
              key: 'status',
              slot: 'status',
              sortable: true,
              content: (item) => item.status,
              style: { textAlign: 'center' },
            },
          },
          {
            header: { content: 'Traffic Source', style: '' },
            display: true,
            item: {
              key: 'type.name',
              slot: 'type',
              searchable: true,
              sortable: true,
              content: (item) => item.type.name,
            },
          },
          {
            header: { content: 'Linked Trackers', style: '' },
            display: true,
            item: {
              key: 'linkedTrackers',
              slot: 'linkedTrackers',
              cellClass: 'linked-trackers-cell',
              content: (item) => item.linkedTrackers.map((tracker) => tracker.name).join(', '),
            },
          },
          {
            header: { content: 'Timezone', style: '' },
            display: true,
            item: {
              key: 'settings.timezone',
              searchable: true,
              sortable: true,
              content: (item) => item.settings.timezone,
              style: { textAlign: 'center' },
            },
          },
          {
            header: { content: 'Currency', style: '' },
            display: true,
            item: {
              key: 'settings.currency',
              searchable: true,
              sortable: true,
              content: (item) => item.settings.currency,
              style: { textAlign: 'center' },
            },
          },
          {
            header: { content: 'Created At', style: '' },
            display: true,
            item: {
              key: 'type.created_at',
              searchable: true,
              sortable: true,
              content: (item) => moment(item.created_at).format('YYYY-MM-DD'),
              style: { textAlign: 'center' },
            },
          },
        ],
        items: [],
      },
    };
  },
  computed: {
    displayedLinkedTrackers() {
      return this.table.items.reduce((linkedTrackers, item) => {
        linkedTrackers[item.id] = uniqBy(item.linkedTrackers, 'id');
        return linkedTrackers;
      }, {});
    },
  },
  async created() {
    /** ***************** Load Config **************** */
    this.config = await configFile.getConfig();
    /** *********************************************** */

    await this.$_callbackHandler();
    await this.$_load();
  },
  methods: {
    getTrafficSourceLogo,
    async $_load() {
      /** ***************** Set Resized Columns from Local Storage **************** */
      this.resizedColumns = this.$settings.resizedColumns.getResizedColumns('trafficSources');
      /** ************************************************************************* */

      try {
        const items = await this.config.api.trafficSources.accounts();
        this.table.items = items;
      } catch (error) {
        this.$n_failNotification({ title: error.response.data.message });
      }
      this.handleEditAccountRequest();
      this.preloader = false;
    },
    $_iconClass(uniqueName) {
      return `my-icon my-${uniqueName.toLowerCase()}`;
    },
    async $_changeStatus(item, status) {
      try {
        const response = await this.config.api.trafficSources.updateStatus(item.id, status);
        if (response.status === 1) {
          this.$n_successNotification({ title: 'Account enabled' });
        } else {
          this.$n_successNotification({ title: 'Account disabled' });
        }
        item.status = status;
      } catch (error) {
        try {
          this.$n_failNotification({ title: error.response.data.message });
        } catch (err) {
          this.$n_failNotification({ title: 'Update failed' });
        }
      }
    },
    getTotalLinkedNumber(tracker, item) {
      return item.linkedTrackers.filter((data) => data.name === tracker.name && tracker.id === data.id).length;
    },
    docUrl() {
      this.$router.push({ path: 'account-wizard' });
    },
    // async $_delete(item) {
    //   try {
    //     const swal = await this.$swal({
    //       title: `Delete "${item.name}" account ?`,
    //       type: 'warning',
    //       showCancelButton: true,
    //       confirmButtonText: 'Yes, delete it!',
    //       cancelButtonText: 'No, keep it',
    //     });
    //     if (swal.value === true) {
    //       this.preloader = true;
    //       try {
    //         const response = await this.config.api.trafficSources.delete(item.id);
    //         this.$n_successNotification({ title: response.message });
    //         this.$_load();
    //       } catch (error) {
    //         const noticeData = {
    //           title: 'Deleting failed',
    //         };
    //         if (error.response && typeof error.response.message !== 'undefined') noticeData.message = error.response.message;
    //         this.$n_failNotification(noticeData);
    //       }
    //       this.preloader = false;
    //     }
    //   } catch (value) {
    //     //
    //   }
    // },
    $_handleColumnsResize(payload) {
      this.$settings.resizedColumns.setResizedColumns('trafficSources', false, payload);
    },
    $_editAccount(item) {
      const account = {
        ...item,
        linkedTrackers: item.linkedTrackers.map((data) => ({ ...data, isPropertyName: item.linkedTrackers.length !== this.displayedLinkedTrackers[item.id].length })),
      };
      this.$refs.refTrafficSourceModal.showModal({ action: 'edit', account });
    },
    $_updateAccount(account) {
      console.log(account);
      if (account) {
        const tableItemIndex = this.table.items.findIndex((item) => item.id === account.id);
        if (tableItemIndex > -1) this.$set(this.table.items, tableItemIndex, account);
        console.log(this.table.items[tableItemIndex], account);
      }
    },
    async $_callbackHandler() {
      // facebook callback handler
      const query = { ...this.$route.query };
      let state;
      if (query && query.state) {
        try {
          state = JSON.parse(query.state);
        // eslint-disable-next-line no-empty
        } catch (err) {
          console.log('Error parsing state', query.state, err);
        }
      }
      if (state && ['Facebook', 'GoogleAds'].includes(state.account_unique_name)) {
        if (query.code) {
          const accountData = JSON.parse(query.state);
          try {
            const id = accountData.account_id;
            const payload = {
              name: decodeURIComponent(accountData.account_name),
              credentials: { accessToken: query.code },
            };
            if (accountData.settings) payload.settings = JSON.parse(accountData.settings || '{}');
            if (accountData.updateAllCredentials) {
              payload.typeId = accountData.account_type_id;
              const accounts = await this.config.api.trafficSources.updateBulkCredentials(payload);
              this.$n_successNotification({ title: `${accounts.filter((e) => !!e.updated).length} Accounts Updated!` });
              this.table.items = accounts;
            } else {
              await this.config.api.trafficSources.update(id, payload);
              this.$n_successNotification({ title: 'Account Updated!' });
            }
          } catch (error) {
            this.$n_failNotification({ title: error.response.data.message || 'An error occurred' });
          }
          delete query.state;
          delete query.code;
        }
        if (query.error) {
          this.$n_failNotification({ title: query.error_description || 'An error occurred' });
          Object.keys(query).forEach((key) => { delete query[key]; });
        }
        this.$router.replace({ query });
      }
      if (state && ['TikTok'].includes(state.account_unique_name)) {
        if (query.auth_code) {
          const accountData = JSON.parse(query.state);
          try {
            const id = accountData.account_id;
            const payload = {
              name: decodeURIComponent(accountData.account_name),
              credentials: { accessToken: query.auth_code },
            };
            if (accountData.settings) payload.settings = JSON.parse(accountData.settings);
            await this.config.api.trafficSources.update(id, payload);
            this.$n_successNotification({ title: 'Account Updated!' });
          } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || 'An error occurred';
            this.$n_failNotification({ title: errorMessage });
          }
          delete query.state;
          delete query.auth_code;
        }
        if (query.error) {
          this.$n_failNotification({ title: query.error_description || 'An error occurred' });
          Object.keys(query).forEach((key) => { delete query[key]; });
        }
        this.$router.replace({ query });
      }
      if (state && ['AmazonDisplay', 'AmazonDSP'].includes(state.account_unique_name)) {
        if (query.code) {
          const accountData = JSON.parse(query.state);
          try {
            const id = accountData.account_id;
            const payload = {
              name: decodeURIComponent(accountData.account_name),
              credentials: { accessToken: query.code, loginUrl: accountData.loginUrl },
            };
            if (accountData.settings) payload.settings = accountData.settings;
            this.$data.loading = true;
            await this.config.api.trafficSources.update(id, payload);
            this.$n_successNotification({ title: 'Account Updated!' });
            this.$data.loading = false;
          } catch (error) {
            this.$data.loading = false;
            this.$n_failNotification({ title: error.response.data.message || 'An error occurred' });
          }
        }
        if (query.error) {
          this.$n_failNotification({ title: query.error_description || 'An error occurred' });
        }
        this.$router.replace({ });
      }

      if (query.account_unique_name && query.account_unique_name === 'Gemini' && query.refresh_token && query.account_name && query.account_type_id && query.account_id) {
        const id = parseInt(query.account_id, 10);
        // Gemini callback account
        try {
          // Update Account
          const payload = {
            name: query.account_name,
            credentials: { api_key: query.refresh_token },
          };
          if (query.settings) payload.settings = JSON.parse(query.settings);
          await this.config.api.trafficSources.update(id, payload);
        } catch (error) {
          try {
            this.$n_failNotification({ title: error.response.data.message });
          } catch (err) {
            this.$n_failNotification({ title: 'An error occurred' });
          }
        }
        delete query.account_unique_name;
        delete query.refresh_token;
        delete query.account_name;
        delete query.account_type_id;
        delete query.account_id;
        delete query.settings;
        this.$router.replace({ query });
      }
    },
    async onAccountNameChange(name, account) {
      try {
        const data = await this.config.api.trafficSources.update(account.id, { name });
        this.$apiStore.trafficSources.accounts.clearCache();
        this.$apiStore.trafficSources.accounts();
        return data;
      } catch {
        return false;
      }
    },
    showAccountId(item) {
      return ['TikTok', 'Facebook', 'GoogleAds'].includes(item.trafficSourceType.uniqueName);
    },
    handleEditAccountRequest() {
      const editAccount = this.table.items.find((item) => item.id?.toString() === (this.$route.query.editAccount ?? ''));
      this.$router.replace({ editAccount: undefined });

      if (editAccount) {
        this.$_editAccount(editAccount);
      }
    },
  },
};
</script>


<style lang="scss">
.traffic-sources-container {
  .table-wrapper {
    td.cell-name a {
      color: #20a8d8;
      text-decoration: none;
      display: inline-block;

      &:hover {
        text-decoration: underline !important;
      }
    }
    td.cell-name:hover a {
      color: #167495;
    }
    .linked-tracker-btn {
      line-height: 1.3rem;
      margin-right: .5rem;

      img {
        width: 1.6rem;
        margin-right: .5rem;
      }

      .badge {
        top: -0.8rem;
        right: -0.6rem;
        height: 1.6rem;
        line-height: 1.2rem;
      }
    }

    .acc-item {
      &-icon {
        width: 2.2rem;
        margin-right: 0.5rem;
        display: inline-block;
      }
      &-name {
        display: inline-block;
      }
    }
  }

    .optimizer-table {
      .table-holder {
        box-shadow: $bs-md;

        &:has(.field .more-items .show) {
          min-height: 40rem;
        }

        .linked-trackers-cell {
          &:has(.more-items .show) {
            overflow: visible;
          }

          .more-items {
            .dropdown-menu {
              .linked-tracker-btn {
                display: inline-block;
              }
            }
          }
        }
      }
    }
    .accountWizardUrl {
      &:hover {
        text-decoration: underline !important;
        color: #4158D0;
        }
      }
    .lead {
      font-size: 17px;
      font-family: 'Roboto', sans-serif;
    }
    .accountId-text{
      color:$color-dark-gray;
    }
  }
</style>
